import { createRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Package, Activity, AlertTriangle, PlusCircle, Share2, X, Sparkles } from 'lucide-react';
import { EmptyState } from '../components/ui/EmptyState';
import { Shell } from '../components/layout/Shell';
import { ItemCard } from '../components/inventory/ItemCard';
import { Ticker } from '../components/pricing/Ticker';
import { ShareCatalogModal } from '../components/community/ShareCatalogModal';
import { SkeletonItemCard } from '../components/ui/Skeleton';
import { useInventoryList } from '../hooks/useInventory';
import { useHealthSummary } from '../hooks/useHealth';
import { useAuth } from '../hooks/useAuth';
import { useAppMode } from '../context/AppModeContext';
import { useVault } from '../context/VaultContext';
import { useTranslation } from '../i18n';
import { Link, useNavigate } from '@tanstack/react-router';
import { Route as rootRoute } from './__root';
import styles from './dashboard.module.css';


export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  component: DashboardPage,
});

function workingValueClass(pct: number | undefined) {
  if (pct === undefined) return '';
  if (pct < 50) return styles.statValueDanger;
  if (pct < 80) return styles.statValueWarning;
  return '';
}

function ClassicDashboard() {
  const { data, isLoading } = useInventoryList({ limit: 8, sortBy: 'createdAt', sortOrder: 'desc' });
  const { data: healthData, isLoading: healthLoading } = useHealthSummary();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { communityEnabled, aiTier } = useAppMode();
  const { activeVault, vaults, isLoading: vaultsLoading, multiVaultEnabled } = useVault();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const [shareBannerDismissed, setShareBannerDismissed] = useState(() => localStorage.getItem('sv-share-banner-dismissed') === '1');
  const [showShareModal, setShowShareModal] = useState(false);

  // Redirect to vault selector if multi-vault is enabled but no vaults exist
  useEffect(() => {
    if (multiVaultEnabled && !vaultsLoading && vaults.length === 0) {
      navigate({ to: '/vaults' });
    }
  }, [multiVaultEnabled, vaultsLoading, vaults.length, navigate]);

  const urgentCount = healthData?.urgentItems.length ?? 0;
  const heroItem = data?.data.find(item => item.imageUrl);

  return (
    <Shell>
      <div className={styles.page}>
        {/* Hero section */}
        {isLoading ? (
          <div className={styles.heroSkeleton} />
        ) : heroItem ? (
          <div className={styles.hero}>
            <img src={heroItem.imageUrl!} alt="" className={styles.heroImage} />
            <div className={styles.heroOverlay} />
            <div className={styles.heroContent}>
              <h1 className={styles.heroTitle}>{t('dashboard.title')}</h1>
              <div className={styles.heroSub}>{activeVault?.name ?? ''}</div>
            </div>
          </div>
        ) : (
          <div className={styles.heroEmpty}>
            <Package size={40} strokeWidth={1.5} />
            <h1 className={styles.heroTitle}>{t('dashboard.title')}</h1>
          </div>
        )}

        {/* Stat pills */}
        {!isLoading && (
          <div className={styles.statPills}>
            <div className={styles.statPill}>
              <Package size={14} strokeWidth={2} />
              <span className={styles.statPillValue}>{data?.total ?? 0}</span>
              <span className={styles.statPillLabel}>{t('dashboard.totalItems')}</span>
            </div>
            <div className={styles.statPill}>
              <Activity size={14} strokeWidth={2} />
              <span className={`${styles.statPillValue} ${workingValueClass(healthData?.workingPercent)}`}>
                {healthData ? `${healthData.workingPercent}%` : '\u2014'}
              </span>
              <span className={styles.statPillLabel}>{t('dashboard.working')}</span>
            </div>
            {urgentCount > 0 && (
              <Link to="/health" className={`${styles.statPill} ${styles.statPillAlert}`}>
                <AlertTriangle size={14} strokeWidth={2} />
                <span className={styles.statPillValue}>{urgentCount}</span>
                <span className={styles.statPillLabel}>{t('dashboard.alerts')}</span>
              </Link>
            )}
          </div>
        )}

        {/* Upgrade banner — show when free tier and approaching item limit */}
        {aiTier !== 'pro' && (data?.total ?? 0) >= 80 && (
          <div className={styles.shareBanner}>
            <Sparkles size={16} strokeWidth={1.75} />
            <div className={styles.shareBannerText}>
              <strong>{t('upgrade.dashboardBannerTitle')}</strong>
              <span>{t('upgrade.dashboardBannerDesc').replace('{count}', String(data?.total ?? 0))}</span>
            </div>
            <Link to="/upgrade" className={styles.shareBannerCta}>
              {t('upgrade.dashboardBannerCta')}
            </Link>
          </div>
        )}

        {/* Share banner */}
        {communityEnabled && !shareBannerDismissed && (
          <div className={styles.shareBanner}>
            <Share2 size={16} strokeWidth={1.75} />
            <div className={styles.shareBannerText}>
              <strong>{t('share.dashboardBannerTitle')}</strong>
              <span>{t('share.dashboardBannerDesc')}</span>
            </div>
            <button className={styles.shareBannerCta} onClick={() => setShowShareModal(true)}>
              {t('share.dashboardBannerCta')}
            </button>
            <button
              className={styles.shareBannerClose}
              onClick={() => { localStorage.setItem('sv-share-banner-dismissed', '1'); setShareBannerDismissed(true); }}
              aria-label="Dismiss"
            >
              <X size={14} strokeWidth={2} />
            </button>
          </div>
        )}

        {/* Recently added */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>
            {t('dashboard.recentlyAdded')}
            <Link to="/inventory" className={styles.sectionLink}>{t('dashboard.viewAll')}</Link>
          </h2>
          {isLoading ? (
            <div className={styles.recentGrid}>
              {Array.from({ length: 8 }).map((_, i) => (
                <SkeletonItemCard key={i} />
              ))}
            </div>
          ) : data?.data.length === 0 ? (
            <EmptyState
              icon={Package}
              title={t('dashboard.emptyTitle')}
              description={t('dashboard.emptyDesc')}
              actionLabel={t('dashboard.addFirstItem')}
              actionTo="/inventory/new"
              actionIcon={PlusCircle}
            />
          ) : (
            <div className={styles.recentGrid}>
              {data?.data.map((item) => (
                <ItemCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </div>
      </div>

      {showShareModal && <ShareCatalogModal onClose={() => setShowShareModal(false)} />}
      <Ticker />
    </Shell>
  );
}

function DashboardPage() {
  return <ClassicDashboard />;
}
